<script setup lang="ts">
import { useExplorerSecondaryDrawer } from "@/modules/explorer/composables/ExplorerSecondaryDrawer";
import axisConfigurationDialog from "@/modules/explorer/components/AxisConfiguration/Dialog.vue";
import { computed, ref } from "vue";
import { useBaseStore } from "@/stores/base";
import { useExplorerStore } from "@/modules/explorer/store";
import DeveloperDialog from "@/modules/explorer/components/DeveloperDialog.vue";
import ConfigureAlertDialog from "@/modules/explorer/components/alerts/ConfigureAlertDialog.vue";
import ChatWindow from "@/ui/components/ai-chat/ChatWindow.vue";
import GridConfigurationDialog from "@/modules/explorer/components/Metrics/GridConfigurationDialog.vue";
import DataTableDO = App.Data.Explorer.DataTableDO;
import { toRawDeep } from "@/Utils/to-raw-deep";
import {
	getCurrentDataTable,
	updateCurrentDataTable,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import DataStreamStatusPanel from "@/modules/explorer/components/DataStreamStatusPanel.vue";
import { effectiveState } from "@/Utils/proxy/DerivedModelProxyHandler";
import { GRAPH_TYPES } from "chartjs-graph";
import { storeToRefs } from "pinia";
import useDialog from "@/ui/composables/useDialog";

const explorerStore = useExplorerStore();
const { explorerState } = storeToRefs(explorerStore);
const dialog = useDialog();

const { openSecondaryDrawer } = useExplorerSecondaryDrawer();
const graphConfigurationIsVisible = ref(false);
const developerDialogIsVisible = ref(false);

const totalProcessingTime = computed(() => {
	const performanceData =
		useExplorerStore().explorerState.lastClickhouseQueryPerformance ?? {}; // Fallback to empty object
	return (
		Math.round(
			Object.values(performanceData).reduce(
				(total, value) => total + (value || 0),
				0
			) * 100
		) / 100
	);
});

const customSqlMode = computed(() => {
	return !!useExplorerStore().document.data_tables[0].custom_sql;
});

const hasAlreadySavedAlert = computed(() => {
	return !!useExplorerStore().explorerState.lastOpenedSavedDocument?.alert?.id;
});

const userCanDefineAlerts = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_alerts"
	);
});

const isAiAssistEnabled = computed(() => {
	return (
		useBaseStore().HAS_ENVIRONMENT_FEATURE("environment_supports_ai_assist") &&
		useBaseStore().currentUser?.derivedPermissions.includes("can_ask_ai")
	);
});

const isAiAssistVisible = computed(() => {
	return (
		isAiAssistEnabled.value &&
		explorerStore.explorerState.activeDataView === "graph"
	);
});

const dataTableToEdit = ref<DataTableDO | null>(null);
const graphConfigurationActiveTab = ref<
	"vertical" | "horizontal" | "offset" | "legend"
>("vertical");
const gridConfigurationActiveTab = ref<"horizontal" | "metrics">("horizontal");
const gridConfigurationIsVisible = ref(false);
const showAlertConfigurationDialog = ref(false);
const showAiChat = ref(false);

const openGridConfiguration = (
	gridConfigurationTab: "horizontal" | "metrics"
) => {
	const currentDataTable = getCurrentDataTable(
		explorerStore.document,
		explorerStore.derivedDocument
	);

	dataTableToEdit.value = structuredClone(
		toRawDeep<DataTableDO>(currentDataTable.value)
	) as DataTableDO;
	gridConfigurationActiveTab.value = gridConfigurationTab;
	gridConfigurationIsVisible.value = true;
};

const updateDataTable = (dataTable: DataTableDO) => {
	updateCurrentDataTable(
		explorerStore.document,
		explorerStore.derivedDocument,
		dataTable
	);
};

const toggleAiContext = () => {
	explorerState.value.showAiDrawer = !explorerState.value.showAiDrawer;
};

const docGraphType = effectiveState(
	() => explorerStore.document,
	() => explorerStore.derivedDocument,
	"graph_type"
);

const disableOffsetButton = computed(() => {
	return docGraphType.value === GRAPH_TYPES.AREA;
});

const openAlertConfigurationWindow = async () => {
	if (!explorerStore.isSavedDocument || explorerStore.isOpenedButUnsaved) {
		await dialog.alert({
			title: "Save your configuration first",
			message:
				"Alerts are linked to a saved configuration. Save this configuration and try again.",
		});
		return;
	}

	if (!hasAlreadySavedAlert.value) {
		await dialog.alert({
			title: "No alert defined yet",
			message:
				"To define an alert, right-click the header of the column you want to monitor in the data table.",
		});
		return;
	}

	showAlertConfigurationDialog.value = true;
};

const hasConfiguredAlert = computed(
	() =>
		explorerStore.explorerState.lastOpenedSavedDocument?.alert?.properties ??
		false
);
</script>

<template>
	<chat-window v-model="showAiChat" />

	<configure-alert-dialog
		v-if="showAlertConfigurationDialog"
		v-model="showAlertConfigurationDialog"
		:column-properties="
			explorerStore.explorerState.lastOpenedSavedDocument?.alert?.properties ??
			null
		"
	/>

	<axis-configuration-dialog
		v-if="graphConfigurationIsVisible"
		v-model="graphConfigurationActiveTab"
		:is-visible="graphConfigurationIsVisible"
		@close-dialog="graphConfigurationIsVisible = false"
	/>

	<grid-configuration-dialog
		v-if="gridConfigurationIsVisible"
		v-model="gridConfigurationActiveTab"
		:is-visible="gridConfigurationIsVisible"
		:data-table-to-edit="dataTableToEdit"
		@update="updateDataTable"
		@close-dialog="gridConfigurationIsVisible = false"
	/>

	<v-bottom-navigation
		elevation="0"
		class="rounded-0"
		style="z-index: 0"
		:active="true"
	>
		<v-spacer />
		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'graph'"
			value="configuration"
			:active="false"
			@click="
				graphConfigurationIsVisible = true;
				graphConfigurationActiveTab = 'vertical';
			"
		>
			<v-icon>mdi-arrow-up</v-icon>

			<span style="white-space: nowrap">Vertical</span>
		</v-btn>
		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'graph'"
			value="configuration"
			:active="false"
			@click="
				graphConfigurationIsVisible = true;
				graphConfigurationActiveTab = 'horizontal';
			"
		>
			<v-icon>mdi-arrow-right</v-icon>

			<span style="white-space: nowrap">Horizontal</span>
		</v-btn>
		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'graph'"
			value="configuration"
			:active="false"
			:disabled="disableOffsetButton"
			@click="
				graphConfigurationIsVisible = true;
				graphConfigurationActiveTab = 'offset';
			"
		>
			<v-icon>mdi-set-split</v-icon>

			<span style="white-space: nowrap">Offset</span>
		</v-btn>
		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'graph'"
			value="configuration"
			:active="false"
			@click="
				graphConfigurationIsVisible = true;
				graphConfigurationActiveTab = 'legend';
			"
		>
			<v-icon>mdi-chart-pie</v-icon>

			<span style="white-space: nowrap">Legend</span>
		</v-btn>

		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'table'"
			value="configuration"
			:active="false"
			:disabled="customSqlMode"
			@click="openGridConfiguration('horizontal')"
		>
			<v-icon>mdi-table-column</v-icon>

			<span style="white-space: nowrap">Dimensions</span>
		</v-btn>

		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'table'"
			value="configuration"
			:active="false"
			icon=""
			:disabled="customSqlMode"
			@click="openGridConfiguration('metrics')"
		>
			<v-icon>mdi-table-row</v-icon>

			<span style="white-space: nowrap">Metrics</span>
		</v-btn>

		<v-divider vertical />

		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'graph'"
			value="settings"
			:active="false"
			:disabled="explorerStore.derivedDocument === null"
			@click="openSecondaryDrawer('DisplaySettings')"
		>
			<v-icon>mdi-tune</v-icon>

			<span>Display Settings </span>
		</v-btn>

		<v-btn
			v-if="explorerStore.explorerState.activeDataView === 'table'"
			value="settings"
			:active="false"
			:disabled="customSqlMode"
			@click="openSecondaryDrawer('DataTableDisplaySettings')"
		>
			<v-icon>mdi-tune</v-icon>

			<span>Display Settings</span>
		</v-btn>

		<v-divider
			v-if="
				userCanDefineAlerts &&
				explorerStore.explorerState.activeDataView === 'table'
			"
			vertical
		/>

		<v-btn
			:active="showAlertConfigurationDialog"
			v-if="
				userCanDefineAlerts &&
				explorerStore.explorerState.activeDataView === 'table'
			"
			@click="openAlertConfigurationWindow"
			value="alerts"
		>
			<v-icon>fa: fa-regular fa-bell-on</v-icon>

			<span>Alert</span>
			<span
				v-if="hasConfiguredAlert"
				class="alert-configured-dot"
				v-tooltip="'An alert is configured'"
			/>
		</v-btn>

		<v-divider vertical v-if="isAiAssistVisible" />
		<v-btn
			v-if="isAiAssistVisible"
			:active="explorerState.showAiDrawer"
			value="AI Assist"
			@click="toggleAiContext"
		>
			<v-icon>fa: fa-light fa-sparkles</v-icon>

			<span>AI Assist</span>
		</v-btn>

		<v-spacer />

		<v-menu open-on-hover>
			<template v-slot:activator="{ props }">
				<v-btn v-bind="props" icon="fa fa-light fa-cloud-binary fa-2xl" />
			</template>
			<DataStreamStatusPanel />
		</v-menu>

		<v-badge
			dot
			style="opacity: 0.5"
			:color="
				totalProcessingTime < 150
					? 'success'
					: totalProcessingTime < 250
					? 'warning'
					: 'error'
			"
			v-if="useBaseStore().currentUser.is_motives_admin"
			offset-y="10"
		/>
		<div style="width: 40px" v-if="useBaseStore().currentUser.is_motives_admin">
			<i
				style="opacity: 0.5; cursor: pointer"
				class="mt-4 fa-light fa-gauge-low fa-2xl"
			>
			</i>
			<v-tooltip activator="parent" location="left">
				<div
					v-for="(measurement, stat) in explorerStore.explorerState
						.lastClickhouseQueryPerformance"
				>
					<strong>{{ stat }}:</strong>
					{{ measurement }}
				</div>
				<div>
					<strong>Sum</strong>
					{{ totalProcessingTime }}
				</div>
			</v-tooltip>
		</div>
		<div
			style="width: 40px"
			@click="developerDialogIsVisible = true"
			v-if="useBaseStore().currentUser.is_motives_admin"
		>
			<i
				style="opacity: 0.5; cursor: pointer"
				class="mt-4 fa-duotone fa-brackets-curly fa-2xl"
			>
			</i>
		</div>
	</v-bottom-navigation>
	<developer-dialog
		v-if="useBaseStore().currentUser.is_motives_admin"
		v-model="developerDialogIsVisible"
	/>
</template>

<style lang="scss" scoped>
.alert-configured-dot {
	position: absolute;
	top: 10px;
	right: 10px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: rgb(var(--v-theme-success));
}
</style>
